import { ReactElement, ReactNode, useEffect, useRef } from "react";

import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import {
    draggable,
    dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { Empty } from "antd";
import { cx } from "class-variance-authority";
import { range } from "lodash-es";

import { ButtonsBehaviour, ElementsAndSectionsBehaviour } from "./behaviours";
import {
    ElementContainer,
    ElementPlaceholderContainer,
    SectionPlaceholderElementContainer,
} from "./element";
import * as theme from "./theme.css";
import {
    SectionSpecId,
    GridLocation,
    PlaceholderPositionAndRender,
    ITEM_TYPES,
    InternalElementAccessors,
} from "./types";

interface DraggedSection {
    type: "SECTION";
    index: number;
    id: string;
}

/**
 * A section in the content panel. A section has at least one column, and can be dragged an dropped. Elements are placed in the columns.
 */
interface SectionProps<
    ELT,
    ElementButtonSpecId extends string,
> extends InternalElementAccessors<ELT> {
    id: SectionSpecId;
    colCount: number;
    index: number;
    elementSpecColumns: Array<Array<ELT>>;
    draggedElementLocation: GridLocation | undefined;
    placeholderPositionAndRender: PlaceholderPositionAndRender | undefined;
    buttonsBehaviour: ButtonsBehaviour<ElementButtonSpecId>;
    elementsAndSectionsBehaviour: ElementsAndSectionsBehaviour;
    renderSection: (children: ReactElement | null) => ReactElement;
    noElementsLabel?: string;
}
function Section<ELT, ElementButtonSpecId extends string>({
    id,
    index,
    colCount,
    renderSection,
    getElementId,
    renderElement,
    draggedElementLocation,
    placeholderPositionAndRender,
    buttonsBehaviour,
    elementsAndSectionsBehaviour,
    elementSpecColumns,
    noElementsLabel,
}: SectionProps<ELT, ElementButtonSpecId>) {
    const enableDnD =
        elementsAndSectionsBehaviour.type === "DRAGGABLE_ELEMENTS_AND_SECTIONS";

    const ref = useRef<HTMLDivElement>(null);

    // Register the section as both draggable and a drop target for other sections.
    useEffect(() => {
        const el = ref.current;
        if (!el || !enableDnD) {
            return;
        }
        const data: DraggedSection = {
            type: ITEM_TYPES.SECTION,
            id,
            index,
        };
        return combine(
            draggable({
                element: el,
                getInitialData: () => ({ ...data }),
            }),
            dropTargetForElements({
                element: el,
                canDrop: ({ source }) =>
                    source.data.type === ITEM_TYPES.SECTION,
                getData: () => ({
                    dropKind: "SECTION" as const,
                    sectionId: id,
                }),
            })
        );
    }, [id, index, enableDnD]);

    return (
        <div
            ref={ref}
            className={cx(
                theme.sectionContainer,
                enableDnD && [theme.sectionHover, theme.draggable]
            )}
        >
            {renderSection(
                <>
                    {range(0, colCount).map((colIndex) => {
                        const column = elementSpecColumns[colIndex] ?? [];
                        const elements: Array<ReactElement> = column.reduce(
                            (acc, elementSpec, row) => {
                                // Do not render the element if we are dragging it around
                                if (
                                    draggedElementLocation &&
                                    draggedElementLocation.column ===
                                        colIndex &&
                                    draggedElementLocation.row === row
                                ) {
                                    return acc;
                                }
                                acc.push(
                                    <ElementContainer
                                        key={row}
                                        id={getElementId(elementSpec)}
                                        position={{
                                            sectionId: id,
                                            location: {
                                                row,
                                                column: colIndex,
                                            },
                                        }}
                                        buttonsBehaviour={buttonsBehaviour}
                                        elementsAndSectionsBehaviour={
                                            elementsAndSectionsBehaviour
                                        }
                                    >
                                        {renderElement(elementSpec)}
                                    </ElementContainer>
                                );
                                return acc;
                            },
                            [] as Array<ReactElement>
                        );
                        // Splice the drag and drop placeholder if needed
                        if (placeholderPositionAndRender) {
                            const { location } =
                                placeholderPositionAndRender.position
                                    .positionAfterDraggedElementIsRemoved;
                            if (location.column === colIndex) {
                                const placeholderElt = (
                                    <ElementPlaceholderContainer key="placeholder">
                                        {placeholderPositionAndRender.render()}
                                    </ElementPlaceholderContainer>
                                );
                                elements.splice(
                                    location.row,
                                    0,
                                    placeholderElt
                                );
                            }
                        }
                        return (
                            <SectionColumn
                                key={colIndex}
                                sectionId={id}
                                index={colIndex}
                                buttonsBehaviour={buttonsBehaviour}
                                elementsAndSectionsBehaviour={
                                    elementsAndSectionsBehaviour
                                }
                            >
                                {elements.length === 0 ? (
                                    <div className={theme.emptyContainer}>
                                        <Empty description={noElementsLabel} />
                                    </div>
                                ) : (
                                    elements
                                )}
                            </SectionColumn>
                        );
                    })}
                </>
            )}
        </div>
    );
}

interface SectionColumnProps<ElementButtonSpecId extends string> {
    sectionId: SectionSpecId;
    index: number;
    buttonsBehaviour: ButtonsBehaviour<ElementButtonSpecId>;
    elementsAndSectionsBehaviour: ElementsAndSectionsBehaviour;
    children?: ReactNode;
}
function SectionColumn<ElementButtonSpecId extends string>(
    props: SectionColumnProps<ElementButtonSpecId>
) {
    const {
        sectionId,
        index,
        buttonsBehaviour,
        elementsAndSectionsBehaviour,
        children,
    } = props;
    const enableDnD =
        buttonsBehaviour.type !== "HIDDEN" ||
        elementsAndSectionsBehaviour.type !== "FIXED";

    const ref = useRef<HTMLDivElement>(null);

    // Register the column as a drop target for buttons and elements.
    useEffect(() => {
        const el = ref.current;
        if (!el || !enableDnD) {
            return;
        }
        return dropTargetForElements({
            element: el,
            canDrop: ({ source }) => {
                const t = source.data.type;
                return t === ITEM_TYPES.BUTTON || t === ITEM_TYPES.ELEMENT;
            },
            getData: () => ({
                dropKind: "COLUMN" as const,
                sectionId,
                column: index,
            }),
        });
    }, [sectionId, index, enableDnD]);

    return (
        <div ref={ref} className={theme.sectionColumn}>
            {children}
        </div>
    );
}

/**
 * A section in the content panel. A section has at least one column, and can be dragged an dropped. Elements are placed in the columns.
 */
function SectionPlaceholder<ELT>(props: SectionPlaceholderProps<ELT>) {
    const {
        colCount,
        renderSectionPlaceholder,
        getElementId,
        renderElement,
        elementSpecColumns,
    } = props;
    return (
        <div className={theme.sectionContainer}>
            {renderSectionPlaceholder(
                <>
                    {range(0, colCount).map((colIndex) => {
                        const column = elementSpecColumns[colIndex] ?? [];
                        return (
                            <div key={colIndex} className={theme.sectionColumn}>
                                {column.map((elementSpec) => (
                                    <SectionPlaceholderElementContainer
                                        key={getElementId(elementSpec)}
                                    >
                                        {renderElement(elementSpec)}
                                    </SectionPlaceholderElementContainer>
                                ))}
                            </div>
                        );
                    })}
                </>
            )}
        </div>
    );
}

interface SectionPlaceholderProps<ELT> extends InternalElementAccessors<ELT> {
    colCount: number;
    elementSpecColumns: Array<Array<ELT>>;
    renderSectionPlaceholder: (
        section: ReactElement | null
    ) => ReactElement | null;
}

export {
    type DraggedSection,
    Section,
    type SectionProps,
    SectionPlaceholder,
    type SectionPlaceholderProps,
};
